import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Modal } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './Portfolio.css';

const Portfolio = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  // Projects data
  const projects = [
    {
      id: 1,
      title: 'E-commerce Platform',
      category: 'Web Development',
      image: 'https://images.unsplash.com/photo-1523800503107-5bc3ba2a6f81?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2080&q=80',
      description: 'A full-featured e-commerce platform with product management, user authentication, shopping cart, and payment integration.',
      technologies: ['React', 'Node.js', 'MongoDB', 'Stripe API'],
      challenges: 'Implementing secure payment processing and optimizing product search functionality for large inventory.',
      solutions: 'Utilized Stripe for secure payments and implemented Elasticsearch for fast and reliable product search.',
      results: 'Increased conversion rate by 25% and reduced page load time by 40%.',
      link: 'https://example.com/ecommerce-project'
    },
    {
      id: 2,
      title: 'Healthcare Mobile App',
      category: 'Mobile Development',
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80',
      description: 'A mobile application for healthcare providers to manage patient information, appointments, and medical records.',
      technologies: ['React Native', 'Firebase', 'HIPAA Compliance'],
      challenges: 'Ensuring data security and HIPAA compliance while maintaining a seamless user experience.',
      solutions: 'Implemented end-to-end encryption and secure authentication protocols.',
      results: 'Reduced administrative work by 30% and improved patient satisfaction scores.',
      link: 'https://example.com/healthcare-app'
    },
    {
      id: 3,
      title: 'Real Estate Website',
      category: 'Web Development',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2073&q=80',
      description: 'A responsive real estate website with property listings, advanced search, and virtual tours.',
      technologies: ['React', 'Three.js', 'Google Maps API', 'Node.js'],
      challenges: 'Implementing interactive 3D property tours and optimizing for mobile devices.',
      solutions: 'Used Three.js for 3D visualization and implemented responsive design principles.',
      results: 'Increased property inquiries by 50% and reduced bounce rate by 35%.',
      link: 'https://example.com/real-estate-project'
    },
    {
      id: 4,
      title: 'Fitness Tracker App',
      category: 'Mobile Development',
      image: 'https://images.unsplash.com/photo-1576678927484-cc907957088c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2073&q=80',
      description: 'A fitness tracking application that allows users to monitor workouts, set goals, and track progress.',
      technologies: ['React Native', 'Redux', 'Health APIs', 'Firebase'],
      challenges: 'Integrating with various health APIs and ensuring accurate data synchronization.',
      solutions: 'Developed a custom middleware for API integrations and implemented offline support.',
      results: 'Achieved 100,000+ downloads with a 4.8-star rating on app stores.',
      link: 'https://example.com/fitness-app'
    },
    {
      id: 5,
      title: 'Financial Dashboard',
      category: 'Web Development',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80',
      description: 'An interactive financial dashboard for visualizing and analyzing business metrics and KPIs.',
      technologies: ['React', 'D3.js', 'Node.js', 'MongoDB'],
      challenges: 'Creating interactive data visualizations and handling large datasets efficiently.',
      solutions: 'Utilized D3.js for visualizations and implemented data aggregation on the backend.',
      results: 'Reduced report generation time by 80% and improved decision-making processes.',
      link: 'https://example.com/financial-dashboard'
    },
    {
      id: 6,
      title: 'Educational Platform',
      category: 'Web Development',
      image: 'https://images.unsplash.com/photo-1501504905252-473c47e087f8?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2074&q=80',
      description: 'An online learning platform with course management, student progress tracking, and interactive content.',
      technologies: ['React', 'Express.js', 'MongoDB', 'WebRTC'],
      challenges: 'Developing real-time collaboration features and ensuring scalability for thousands of users.',
      solutions: 'Implemented WebRTC for live sessions and optimized database queries for performance.',
      results: 'Supported 50,000+ students with a 40% increase in course completion rates.',
      link: 'https://example.com/educational-platform'
    }
  ];

  // Filter categories
  const categories = ['All', 'Web Development', 'Mobile Development', 'UI/UX Design'];
  const [activeCategory, setActiveCategory] = useState('All');

  // Filtered projects
  const filteredProjects = activeCategory === 'All' 
    ? projects 
    : projects.filter(project => project.category === activeCategory);

  // Modal handling
  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Intersection observer hooks
  const [headerRef, headerInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [filterRef, filterInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [projectsRef, projectsInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <div className="portfolio-page">
      {/* Page Header */}
      <section className="portfolio-header" ref={headerRef}>
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={8} md={10}>
              <motion.div
                initial="hidden"
                animate={headerInView ? "visible" : "hidden"}
                variants={fadeIn}
              >
                <h1 className="page-title">Our Portfolio</h1>
                <p className="lead">
                  Explore our latest projects and see how we've helped businesses achieve their digital goals.
                </p>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Portfolio Filters */}
      <section className="filter-section section-padding" ref={filterRef}>
        <Container>
          <motion.div
            initial="hidden"
            animate={filterInView ? "visible" : "hidden"}
            variants={fadeIn}
            className="text-center"
          >
            <div className="filter-buttons">
              {categories.map((category, index) => (
                <Button
                  key={index}
                  variant={activeCategory === category ? 'primary' : 'outline-primary'}
                  className={`filter-btn ${activeCategory === category ? 'active' : ''}`}
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </Button>
              ))}
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Portfolio Projects */}
      <section className="projects-section section-padding" ref={projectsRef}>
        <Container>
          <motion.div
            initial="hidden"
            animate={projectsInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <Row>
              {filteredProjects.map((project) => (
                <Col lg={4} md={6} className="mb-4" key={project.id}>
                  <motion.div variants={fadeIn}>
                    <Card className="project-card card-hover">
                      <div className="project-image-container">
                        <Card.Img variant="top" src={project.image} className="project-image" />
                        <div className="project-overlay">
                          <Button 
                            variant="light" 
                            className="view-project-btn"
                            onClick={() => handleProjectClick(project)}
                          >
                            View Project
                          </Button>
                        </div>
                      </div>
                      <Card.Body>
                        <div className="project-category">{project.category}</div>
                        <Card.Title className="project-title">{project.title}</Card.Title>
                        <Card.Text className="project-description">{project.description}</Card.Text>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Project Detail Modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="lg"
        centered
        className="project-modal"
      >
        {selectedProject && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>{selectedProject.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="modal-image-container mb-4">
                <img src={selectedProject.image} alt={selectedProject.title} className="img-fluid rounded" />
              </div>
              <h5>Project Overview</h5>
              <p>{selectedProject.description}</p>
              
              <h5>Technologies Used</h5>
              <div className="technologies-list mb-4">
                {selectedProject.technologies.map((tech, index) => (
                  <span key={index} className="technology-badge">{tech}</span>
                ))}
              </div>
              
              <Row className="mb-4">
                <Col md={4}>
                  <h5>Challenges</h5>
                  <p>{selectedProject.challenges}</p>
                </Col>
                <Col md={4}>
                  <h5>Solutions</h5>
                  <p>{selectedProject.solutions}</p>
                </Col>
                <Col md={4}>
                  <h5>Results</h5>
                  <p>{selectedProject.results}</p>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={handleCloseModal}>
                Close
              </Button>
              {/* <Button 
                variant="primary" 
                className="btn-gradient"
                href={selectedProject.link}
                target="_blank"
              >
                Visit Project
              </Button> */}
            </Modal.Footer>
          </>
        )}
      </Modal>
    </div>
  );
};

export default Portfolio; 